/**
 * Pure H OS · Inventory · Warehouse detail (header · bins · on-hand).
 */
import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Plus, Trash2 } from "lucide-react";
import { inventory, InvWarehouse, InvBin, InvStockItem } from "../../services/api";

const VENTURE = "orvan";

export default function WarehouseDetail() {
  const { id } = useParams<{ id: string }>();
  const [wh, setWh] = useState<InvWarehouse | null>(null);
  const [bins, setBins] = useState<InvBin[]>([]);
  const [stock, setStock] = useState<InvStockItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<"bins" | "stock">("bins");
  const [showAddBin, setShowAddBin] = useState(false);
  const [binForm, setBinForm] = useState({ code: "", capacity: "", accessNotes: "" });

  const reload = useCallback(async () => {
    if (!id) return;
    try {
      const [w, b, s] = await Promise.all([
        inventory.getWarehouse(VENTURE, id),
        inventory.listBins(VENTURE, id),
        inventory.listStock(VENTURE, { warehouseId: id }),
      ]);
      setWh(w);
      setBins(b);
      setStock(s);
    } catch (e) {
      setError((e as Error).message);
    }
  }, [id]);

  useEffect(() => { reload(); }, [reload]);

  async function submitBin() {
    if (!id) return;
    try {
      await inventory.createBin(VENTURE, id, {
        code: binForm.code,
        capacity: binForm.capacity ? Number(binForm.capacity) : undefined,
        accessNotes: binForm.accessNotes || undefined,
      });
      setShowAddBin(false);
      setBinForm({ code: "", capacity: "", accessNotes: "" });
      reload();
    } catch (e) {
      alert((e as Error).message);
    }
  }

  async function deleteBin(binId: string) {
    if (!confirm("Delete this bin? Only allowed if empty.")) return;
    try {
      await inventory.deleteBin(VENTURE, binId);
      reload();
    } catch (e) {
      alert((e as Error).message);
    }
  }

  if (error) return <div className="text-sm text-red-600" data-testid="wh-detail-error">{error}</div>;
  if (!wh) return <div className="text-xs text-slate-400" data-testid="wh-detail-loading">Loading…</div>;

  return (
    <section data-testid="warehouse-detail">
      <Link
        to="/pureh/inventory/warehouses"
        className="text-xs text-slate-500 hover:text-slate-800 inline-flex items-center gap-1 mb-3"
        data-testid="wh-back-link"
      >
        <ArrowLeft size={12} /> All warehouses
      </Link>

      <div className="mb-6" style={{ background: "var(--ph-surface-card)", border: "1px solid var(--ph-border-subtle)", borderRadius: "var(--ph-radius-md)" }}>
        <div className="p-5">
          <div className="text-[11px] font-mono text-slate-400">{wh.docNumber}</div>
          <h2 className="text-xl font-serif mt-1" style={{ color: "var(--ph-ink-strong)" }} data-testid="wh-name">
            {wh.name}
          </h2>
          <div className="text-xs text-slate-500 mt-1" data-testid="wh-type">{wh.type.replace(/_/g, " ")}</div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-4 text-xs">
            <Meta label="Address" value={wh.address} />
            <Meta label="Cost center" value={wh.costCenterId} mono />
            <Meta label="Responsible" value={wh.responsibleUserId} />
            <Meta label="Status" value={wh.active ? "Active" : "Inactive"} />
            <Meta label="Notes" value={wh.notes} span={3} />
          </div>
        </div>
      </div>

      <div className="inline-flex items-center gap-1 p-1 mb-4" style={{ background: "var(--ph-surface)", border: "1px solid var(--ph-border-subtle)", borderRadius: "var(--ph-radius-md)" }}>
        {[
          { k: "bins", label: `Bins (${bins.length})`, testId: "wh-tab-bins" },
          { k: "stock", label: `On-hand (${stock.length})`, testId: "wh-tab-stock" },
        ].map((t) => (
          <button
            key={t.k}
            data-testid={t.testId}
            onClick={() => setTab(t.k as "bins" | "stock")}
            className="px-3 py-1.5 text-xs font-medium"
            style={{
              background: tab === t.k ? "var(--ph-surface-card)" : "transparent",
              color: tab === t.k ? "var(--ph-ink-strong)" : "var(--ph-ink-muted)",
              borderRadius: "var(--ph-radius-sm)",
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "bins" && (
        <div data-testid="wh-bins">
          <div className="flex justify-end mb-3">
            <button
              data-testid="bin-new-btn"
              onClick={() => setShowAddBin(true)}
              className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-white"
              style={{ background: "var(--ph-brand-primary)", borderRadius: "var(--ph-radius-sm)" }}
            >
              <Plus size={13} /> Add bin
            </button>
          </div>
          <div className="overflow-hidden" style={{ background: "var(--ph-surface-card)", border: "1px solid var(--ph-border-subtle)", borderRadius: "var(--ph-radius-md)" }}>
            <table className="w-full text-sm">
              <thead className="text-xs text-slate-500 uppercase tracking-wider" style={{ background: "var(--ph-surface)" }}>
                <tr>
                  <th className="text-left px-3 py-2">Doc #</th>
                  <th className="text-left px-3 py-2">Code</th>
                  <th className="text-left px-3 py-2">Capacity</th>
                  <th className="text-left px-3 py-2">Notes</th>
                  <th className="text-right px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {bins.length === 0 && (
                  <tr><td colSpan={5} className="px-3 py-6 text-center text-slate-400 text-xs">No bins yet.</td></tr>
                )}
                {bins.map((b) => (
                  <tr key={b.id} data-testid={`bin-row-${b.id}`} style={{ borderTop: "1px solid var(--ph-border-subtle)" }}>
                    <td className="px-3 py-2 font-mono text-xs text-slate-500">{b.docNumber}</td>
                    <td className="px-3 py-2 font-medium">{b.code}</td>
                    <td className="px-3 py-2 text-xs">{b.capacity ?? "—"}</td>
                    <td className="px-3 py-2 text-xs text-slate-500">{b.accessNotes || "—"}</td>
                    <td className="px-3 py-2 text-right">
                      <button
                        data-testid={`bin-delete-${b.id}`}
                        onClick={() => deleteBin(b.id)}
                        className="text-xs text-slate-400 hover:text-red-600 inline-flex items-center gap-1"
                      >
                        <Trash2 size={11} /> Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "stock" && (
        <div data-testid="wh-stock">
          <div className="overflow-hidden" style={{ background: "var(--ph-surface-card)", border: "1px solid var(--ph-border-subtle)", borderRadius: "var(--ph-radius-md)" }}>
            <table className="w-full text-sm">
              <thead className="text-xs text-slate-500 uppercase tracking-wider" style={{ background: "var(--ph-surface)" }}>
                <tr>
                  <th className="text-left px-3 py-2">Item</th>
                  <th className="text-left px-3 py-2">Lot</th>
                  <th className="text-left px-3 py-2">Qty</th>
                  <th className="text-left px-3 py-2">Unit cost</th>
                  <th className="text-left px-3 py-2">Received</th>
                </tr>
              </thead>
              <tbody>
                {stock.length === 0 && (
                  <tr><td colSpan={5} className="px-3 py-6 text-center text-slate-400 text-xs">No stock in this warehouse.</td></tr>
                )}
                {stock.map((s) => (
                  <tr key={s.id} data-testid={`stock-row-${s.id}`} style={{ borderTop: "1px solid var(--ph-border-subtle)" }}>
                    <td className="px-3 py-2 font-mono text-xs">{s.itemRefId}</td>
                    <td className="px-3 py-2 text-xs">{s.lotLabel || "—"}</td>
                    <td className="px-3 py-2 font-medium">{s.quantity} {s.unit}</td>
                    <td className="px-3 py-2 text-xs">{s.unitCost?.baseCurrency} {s.unitCost?.baseAmount?.toFixed(2)}</td>
                    <td className="px-3 py-2 text-xs text-slate-500">{new Date(s.receivedAt).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showAddBin && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm" onClick={() => setShowAddBin(false)}>
          <div className="w-full max-w-sm p-6 mx-4 shadow-xl" style={{ background: "white", borderRadius: "var(--ph-radius-md)" }} onClick={(e) => e.stopPropagation()} data-testid="bin-new-dialog">
            <h3 className="text-base font-medium mb-4">New bin</h3>
            <div className="space-y-3">
              <label className="block">
                <div className="text-[11px] uppercase tracking-[0.14em] text-slate-500 mb-1">Code</div>
                <input data-testid="bin-form-code" className="w-full px-2 py-1.5 text-sm border rounded" value={binForm.code} onChange={(e) => setBinForm((f) => ({ ...f, code: e.target.value }))} />
              </label>
              <label className="block">
                <div className="text-[11px] uppercase tracking-[0.14em] text-slate-500 mb-1">Capacity (optional)</div>
                <input data-testid="bin-form-capacity" type="number" className="w-full px-2 py-1.5 text-sm border rounded" value={binForm.capacity} onChange={(e) => setBinForm((f) => ({ ...f, capacity: e.target.value }))} />
              </label>
              <label className="block">
                <div className="text-[11px] uppercase tracking-[0.14em] text-slate-500 mb-1">Access notes</div>
                <input data-testid="bin-form-notes" className="w-full px-2 py-1.5 text-sm border rounded" value={binForm.accessNotes} onChange={(e) => setBinForm((f) => ({ ...f, accessNotes: e.target.value }))} />
              </label>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button data-testid="bin-form-cancel" onClick={() => setShowAddBin(false)} className="px-3 py-1.5 text-xs border rounded">Cancel</button>
              <button
                data-testid="bin-form-save"
                onClick={submitBin}
                disabled={!binForm.code.trim()}
                className="px-3 py-1.5 text-xs font-medium text-white"
                style={{ background: "var(--ph-brand-primary)", borderRadius: "var(--ph-radius-sm)" }}
              >Create</button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function Meta({ label, value, mono, span }: { label: string; value?: string | null; mono?: boolean; span?: number }) {
  return (
    <div style={{ gridColumn: span ? `span ${span}` : undefined }}>
      <div className="text-[10px] uppercase tracking-[0.14em] text-slate-400 mb-0.5">{label}</div>
      <div className={mono ? "font-mono" : ""} style={{ color: value ? "var(--ph-ink-strong)" : "var(--ph-ink-muted)" }}>
        {value || "—"}
      </div>
    </div>
  );
}
